import {
  NavigationMenuRoot,
  NavigationMenuViewport,
} from "@/components/ui/NavigationMenu";
import { cn } from "@/lib/utils";
import React from "react";
import { useLocale } from "../../helpers/translations";
import { generateLocalizedPath } from "./helpers/links";
import { RAILS_ROUTES } from "./helpers/routes";
import VentionLogoSmall from "../core/VentionLogoSmall";
import { DesktopNavigation } from "./DesktopNavigation";
import { MobileNavigation } from "./MobileNavigation";
import { CartStatus } from "./utility-links/CartStatus";
import { MobileSignupAvatar } from "./utility-links/MobileSignupAvatar";
import { UtilityLinks } from "./utility-links/UtilityLinks";

export const MainNavigation = ({
  className,
}: {
  readonly className?: string;
}) => {
  const locale = useLocale();
  const homePath = generateLocalizedPath(RAILS_ROUTES.HOME, locale);

  return (
    <NavigationMenuRoot
      delayDuration={50}
      className={cn(
        "bg-gray-light-800 relative w-full z-10",
        "shadow-md [&:has([data-state=open])]:shadow-none",
        "max-lg:overflow-x-hidden",
        className,
      )}
    >
      <div className="container h-[var(--header-nav-height)] flex justify-between items-center">
        <div className="flex flex-grow items-center">
          {/* Small V-checkmark: lg (1024px) → 1120px */}
          <a
            href={homePath}
            className="max-lg:hidden min-[1280px]:hidden py-md"
          >
            <VentionLogoSmall className="fill-white h-7" />
          </a>

          {/* Full desk logo: 1120px → 2xl (1536px) */}
          <a href={homePath} className="max-[1279px]:hidden 2xl:hidden py-md">
            <img
              src="https://assets.vention.io/marketing/page-assets/MegaMenu/Vention_MegaMenu_Logo_Desk.svg"
              alt="Vention"
              className="h-2xl block"
            />
          </a>

          {/* Full desk logo: mobile (below lg) + 2xl+ */}
          <a
            className="max-lg:flex max-2xl:hidden max-xs:py-md max-xs:pr-xxs py-lg mt-[-3px]"
            href={homePath}
          >
            <img
              src="https://assets.vention.io/marketing/page-assets/MegaMenu/Vention_MegaMenu_Logo_Desk.svg"
              alt="Vention"
              className="h-2xl block"
            />
          </a>

          <DesktopNavigation
            // adjust desktop on specific breakpoints and language to make it fit responsive;
            // the nav is present on every page, so make it look pretty.
            className={cn(
              "max-lg:hidden",

              // german
              "max-[1420px]:[html[lang=de]_&_.main-navigation-menu-trigger]:px-lg",
              "max-[1360px]:[html[lang=de]_&_.main-navigation-menu-trigger]:px-md",
              "max-[1320px]:[html[lang=de]_&_.main-navigation-menu-trigger]:px-sm",
              "max-[1300px]:[html[lang=de]_&_.main-navigation-menu-trigger]:px-xs",
              "max-[1279px]:[html[lang=de]_&_.main-navigation-menu-trigger]:px-lg",
              "max-[1240px]:[html[lang=de]_&_.main-navigation-menu-trigger]:px-md",
              "max-[1160px]:[html[lang=de]_&_.main-navigation-menu-trigger]:px-sm",
              "max-[1120px]:[html[lang=de]_&_.main-navigation-menu-trigger]:px-xs",
              "max-[1100px]:[html[lang=de]_&_.main-navigation-menu-trigger]:px-xxs",

              // french
              "max-[1420px]:[html[lang=fr]_&_.main-navigation-menu-trigger]:px-lg",
              "max-[1320px]:[html[lang=fr]_&_.main-navigation-menu-trigger]:px-md",
              "max-[1279px]:[html[lang=fr]_&_.main-navigation-menu-trigger]:px-lg",
              "max-[1240px]:[html[lang=fr]_&_.main-navigation-menu-trigger]:px-md",
              "max-[1120px]:[html[lang=fr]_&_.main-navigation-menu-trigger]:px-sm",
              "max-[1100px]:[html[lang=fr]_&_.main-navigation-menu-trigger]:px-xs",

              // english
              "max-[1279px]:[&_.main-navigation-menu-trigger]:px-lg",
            )}
          />
        </div>

        <div className="lg:hidden flex items-center">
          <MobileSignupAvatar />
          <CartStatus />
          <MobileNavigation />
        </div>

        <UtilityLinks className="max-lg:hidden h-[var(--header-nav-height)] overflow-x-hidden" />
      </div>

      <NavigationMenuViewport
        wrapperClassName="w-full justify-start"
        className="md:w-full mt-0 max-h-[calc(100dvh-var(--header-nav-height)-8px)] overflow-y-auto overscroll-contain"
      />
    </NavigationMenuRoot>
  );
};
